import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import BoardPreview from "./BoardPreview";
import RuleChecks from "./RuleChecks";
import Icon from "../../common/Icon";
import { VerticalLine } from "../../../constants/Styles";
import * as styledElements from "./ReviewBoardStyles";

const SIDEBAR_TOP = 16;

const ReviewBoard = () => {
  const [selectedRef, setSelectedRef] = useState<HTMLDivElement | null>(null);
  const parentRef = useRef<HTMLDivElement | null>(null);
  const sidebarRef = useRef<HTMLDivElement | null>(null);
  const drcRef = useRef<HTMLDivElement | null>(null);
  const previewsRef = useRef<HTMLDivElement | null>(null);

  const sidebarInfo = useMemo(
    () => [
      {
        title: "Design Rule Checks",
        icon: "/icons/circle_check.svg",
        ref: drcRef,
        component: RuleChecks,
      },
      {
        title: "Previews",
        icon: "/icons/eye.svg",
        ref: previewsRef,
        component: BoardPreview,
      },
    ],
    [drcRef, previewsRef],
  );

  const determineSelectedRef = useCallback(() => {
    setSelectedRef(
      sidebarInfo.find((tab) => {
        if (tab.ref.current !== null) {
          // Its bottom is below the top of the screen
          return tab.ref.current.getBoundingClientRect().bottom > 0;
        }
        return false;
      })?.ref.current || null,
    );
    if (sidebarRef.current !== null && parentRef.current !== null) {
      const parentTopPosition = parentRef.current.getBoundingClientRect().top;
      if (parentTopPosition < SIDEBAR_TOP) {
        sidebarRef.current.style.marginTop = `${
          SIDEBAR_TOP - parentTopPosition
        }px`;
      }
    }
  }, [sidebarInfo]);

  useEffect(() => {
    window.addEventListener("scroll", determineSelectedRef);
    return () => {
      window.removeEventListener("scroll", determineSelectedRef);
    };
  }, [determineSelectedRef]);

  useEffect(() => {
    determineSelectedRef();
  }, [determineSelectedRef]);

  return (
    <styledElements.Container ref={parentRef}>
      <styledElements.Sidebar ref={sidebarRef}>
        {/* eslint-disable-next-line react-hooks/refs*/}
        {sidebarInfo.map((tab) => {
          const isSelected =
            selectedRef !== null && tab.ref.current == selectedRef;
          return (
            <styledElements.SidebarRow
              key={tab.title}
              isSelected={isSelected}
              onClick={() => {
                if (tab.ref.current !== null) {
                  tab.ref.current.scrollIntoView({
                    behavior: "smooth",
                    block: "start",
                  });
                }
              }}
            >
              {isSelected && <VerticalLine />}
              <styledElements.IconContainer>
                <Icon src={tab.icon} alt={tab.title} iconDimension={16} />
              </styledElements.IconContainer>
              <div>{tab.title}</div>
            </styledElements.SidebarRow>
          );
        })}
      </styledElements.Sidebar>
      <styledElements.Body>
        {/* eslint-disable-next-line react-hooks/refs*/}
        {sidebarInfo.map((tab, index) => (
          <styledElements.Section key={tab.title} ref={tab.ref}>
            <styledElements.SubTitle isSecondElement={index == 1}>
              {tab.title}
            </styledElements.SubTitle>
            <tab.component />
          </styledElements.Section>
        ))}
      </styledElements.Body>
    </styledElements.Container>
  );
};

export default ReviewBoard;
